import React, { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { errorLogger } from '@/lib/error-logger';
import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  pageName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Page-level error boundary for isolating errors to specific routes
 * This prevents a single page error from crashing the entire app
 */
export class PageErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  async componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const { pageName = 'Unknown Page' } = this.props;

    // Log to Sentry with page context
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
        page: {
          name: pageName,
        },
      },
      tags: {
        errorBoundary: 'page',
        page: pageName,
      },
    });

    // Log error to our error logging service
    await errorLogger.log(error, {
      severity: 'error',
      componentStack: errorInfo.componentStack,
      additionalContext: {
        errorBoundary: 'page',
        pageName,
      },
    });

    // Notify admins of critical errors via edge function
    try {
      const { supabase } = await import('@/integrations/supabase/client');
      
      await supabase.functions.invoke('notify-admin-error', {
        body: {
          title: `Page Error: ${pageName}`,
          message: error.message,
          errorStack: error.stack,
          pageName,
          severity: 'high',
          metadata: {
            componentStack: errorInfo.componentStack,
            errorBoundary: 'page',
          },
        },
      });
    } catch (notifyError) {
      // Don't throw - error logging is already handled above
      console.error('Failed to notify admins:', notifyError);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      const { pageName = 'this page' } = this.props;

      return (
        <div className="flex items-center justify-center min-h-[400px] p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning/10">
                  <AlertTriangle className="h-5 w-5 text-warning" />
                </div>
                <div>
                  <CardTitle className="text-lg">Page Error</CardTitle>
                  <CardDescription>
                    Error loading {pageName}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                We've been notified. Try refreshing or go back to your dashboard.
              </p>

              {import.meta.env.DEV && this.state.error && (
                <details className="rounded-lg bg-muted p-3">
                  <summary className="cursor-pointer font-medium text-xs mb-2">
                    Error Details (Dev Only)
                  </summary>
                  <pre className="text-xs overflow-auto max-h-40">
                    {this.state.error.toString()}
                  </pre>
                </details>
              )}

              <div className="flex gap-2">
                <Button onClick={this.handleReset} variant="default" className="flex-1">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Retry
                </Button>
                <Button onClick={this.handleGoHome} variant="outline" className="flex-1">
                  <Home className="h-4 w-4 mr-2" />
                  Dashboard
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}
